import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
} from "@mui/material";
import { useGlobalStyles } from "./globalStyles";
import FieldRenderer from "./FieldRenderer";
import { commonStrings } from "./StringBundle";
import { Box, TableFooter } from "@material-ui/core";
import { Link } from "react-router-dom";
import Loading from "./Order/Loading";

const TableField = ({
  columns = [],
  rows = [],
  noDataText = undefined,
  stickyHeader = false,
  maxHeight = undefined,
  size = "small",
  sx = {},
  control = undefined,
  name = undefined,
  hasRowSelection = false,
  idKeyName = "id",
  linkTemplate = undefined,
  hasFooter = false,
  footerTemplate = undefined,
  footerAlignColumn = undefined,
  columnGroups = undefined,
  enableSelectionView = false,
  handleRowSelect = undefined,
  selectedRows = [],
  loading = false,
  error = null,
  rowHeight = undefined,
  fullwidth = false,
}) => {
  const classes = useGlobalStyles();
  const [displayRows, setDisplayRows] = useState(rows);

// set header span
  const rowSpanSkipFlags = columnGroups
    ? columnGroups.flatMap((group) => Array(group.colSpan).fill(group.rowSpan > 1))
    : [];

function handleRowSelection(rowId) {
handleRowSelect(rowId);
}

useEffect(() => {
  if(enableSelectionView ){
    const filterRows = rows.filter((row) => selectedRows.includes(row[idKeyName]));
    setDisplayRows(filterRows);
  }else {
    setDisplayRows(rows);
  }
}, [enableSelectionView, rows, selectedRows]);

  return (
    <Box>
      <TableContainer
        component={Paper}
        className={`${classes.tableFieldMain} ${fullwidth ? classes.containerFull : ""}`}
        sx={{
          maxHeight,
          ...(stickyHeader ? { overflow: "auto" } : {}),
          ...sx,
        }}
      >
        <Table size={size} sx={stickyHeader ? { borderCollapse: "separate" } : {}}>
          <TableHead sx={stickyHeader ? { position: "sticky", top: 0, zIndex: 2 } : {}}>
            {columnGroups ? (
              <>
                <TableRow className="tableFieldColumnGroupRow">
                  {hasRowSelection && <TableCell padding="checkbox" rowSpan={2}></TableCell>}
                  {columnGroups.map((group, index) => (
                    <TableCell
                      key={index}
                      colSpan={group.colSpan}
                      rowSpan={group.rowSpan || 1}
                      align={group.align || "center"}
                    >
                      {group.label}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {columns.map(
                    (col, index) =>
                      !rowSpanSkipFlags[index] && (
                        <TableCell key={index} align={col.align || "left"} sx={col.sx || {}}>
                          {col.label}
                        </TableCell>
                      ),
                  )}
                </TableRow>
              </>
            ) : (
              <TableRow>
                {hasRowSelection && <TableCell padding="checkbox"></TableCell>}
                {columns.map((col, index) => (
                  <TableCell key={index} align={col.align || "left"} sx={col.sx || {}}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <Loading />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ color: "error.main" }}>
                  {error}
                </TableCell>
              </TableRow>
            ) : displayRows?.length ? (
              displayRows?.map((row, rowIndex) => (
                <TableRow key={rowIndex} sx={{ height: rowHeight }}>
                  {hasRowSelection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.includes(row[idKeyName])}
                        onChange={() => handleRowSelection(row[idKeyName])}
                      />
                    </TableCell>
                  )}

                  {columns.map((col, colIndex) => {
                    const cellData = row[col.key];

                    if (col.type === "link") {
                      const url = linkTemplate?.replace("{id}", row[idKeyName]);
                      return (
                        <TableCell key={colIndex} align={col.align || "left"} sx={col.sx || {}}>
                          <Link to={url}>{cellData || "-"}</Link>
                        </TableCell>
                      );
                    }
                    if (React.isValidElement(cellData)) {
                      return (
                        <TableCell key={colIndex} align={col.align || "left"} sx={col.sx || {}}>
                          {cellData}
                        </TableCell>
                      );
                    }
                    if (
                      typeof cellData === "object" &&
                      cellData?.type &&
                      cellData.type !== "static"
                    ) {
                      return (
                        <TableCell key={colIndex} align={col.align || "left"} sx={col.sx || {}}>
                          <FieldRenderer
                            field={{
                              ...cellData,
                              name: `${name}[${rowIndex}].${cellData.name}`,
                            }}
                            control={control}
                          />
                        </TableCell>
                      );
                    } else {
                      return (
                        <TableCell key={colIndex} align={col.align || "left"} sx={col.sx || {}}>
                          {typeof cellData === "object" && cellData?.value
                            ? cellData.value
                            : (cellData ?? "-")}
                        </TableCell>
                      );
                    }
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  {noDataText || commonStrings.noRecordsFound}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {hasFooter && (
            <TableFooter>
              <TableRow className={stickyHeader ? classes.stickyFooter : undefined}>
                {Array.isArray(footerTemplate) ? (
                  footerTemplate
                ) : footerAlignColumn === undefined ? (
                  <TableCell colSpan={columns.length + 1}>{footerTemplate}</TableCell>
                ) : (
                  <>
                    {hasRowSelection && <TableCell padding="checkbox"></TableCell>}
                    {columns.map((col, index) => (
                      <TableCell key={index} align={col.align || "left"}>
                        {index === footerAlignColumn ? footerTemplate : null}
                      </TableCell>
                    ))}
                  </>
                )}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TableField;
